<template>
  <div :class="containerClass">

    <h2>Add an Activity</h2>

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group label="Activity Name*" label-for="name" label-class="form-label">
        <b-form-input
          id="name"
          placeholder="e.g. Hospital Volunteer Program"
          v-model="name"
          :state="!$v.name.required && $v.name.$dirty ? false : null"
        />
        <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Type*" label-for="type" label-class="form-label">
        <v-select
          id="type"
          class="form-control v-select-custom"
          :class="!$v.type.required && $v.type.$dirty ? 'form-control is-invalid' : ''"
          v-model="type"
          :options="activityTypes"
          placeholder="Select a type"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" :required="!type" v-bind="attributes" v-on="events" />
          </template>
        </v-select>
        <b-form-invalid-feedback :state="!$v.type.required && $v.type.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group v-if="type === 'Other'" label="Other" label-for="typeOther" label-class="form-label">
        <b-form-input
          id="typeOther"
          placeholder="Enter type"
          v-model="typeOther"
          :state="!$v.typeOther.required && $v.typeOther.$dirty ? false : null"
        />
        <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Supervisor Name" label-for="supervisorName" label-class="form-label">
        <b-form-input id="supervisorName" placeholder="Enter supervisor name" v-model="supervisorName" />
      </b-form-group>

      <b-form-group label="Organization" label-for="organization" label-class="form-label">
        <b-form-input id="organization" placeholder="Enter organization" v-model="organization" />
      </b-form-group>

      <b-form-group label="Hours" label-for="hours" label-class="form-label">
        <b-form-input id="hours" type="number" min="0" v-model.number="hours" />
      </b-form-group>

      <b-form-group label="Description" label-for="description" label-class="form-label">
        <b-form-textarea id="description" rows="3" v-model="description" placeholder="Briefly describe the activity" />
      </b-form-group>

      <b-btn variant="primary" :disabled="isLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? $t('update') : 'Add' }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import { get } from 'lodash';
import { mapActions } from 'vuex';
import Page from '@/components/Page.vue';
import { required, requiredIf } from 'vuelidate/lib/validators';

export default {
  extends: Page,

  data() {
    return {
      title: 'Add Activity',
      isLoading: false,
      name: '',
      type: null,
      typeOther: '',
      supervisorName: '',
      organization: '',
      hours: 0,
      description: '',
      activityTypes: [
        'Shadowing / Clinical Observation',
        'Volunteer - Medical / Clinical',
        'Volunteer - Non-Clinical',
        'Research / Lab',
        'Publications',
        'Presentations / Posters',
        'Teaching / Tutoring',
        'Employment - Medical / Clinical',
        'Employment - Non-Clinical',
        'Leadership',
        'Club / Extracurricular',
        'Honors / Awards',
        'Conferences Attended',
        'Social Justice / Advocacy',
        'Other',
      ],
    };
  },

  validations() {
    return {
      name: { required },
      type: { required },
      typeOther: { required: requiredIf(() => this.type === 'Other') },
    };
  },

  computed: {
    checklistContext() {
      const uniId = Number(this.$route.query.uniId);
      const programId = Number(this.$route.query.program_id);
      return {
        uniId: Number.isFinite(uniId) && uniId > 0 ? uniId : null,
        programId: Number.isFinite(programId) && programId > 0 ? programId : null,
      };
    },
  },

  methods: {
    ...mapActions('student', [
      'createActivity',
      'getActivity',
      'updateActivity',
      'updateShowEarnedPointToast',
    ]),
    get,
    goBack() {
      this.$router.go(-1);
    },
    async fetchExisting() {
      try {
        const res = await this.getActivity(this.$route.params.id);
        this.name = res.data.name;
        this.type = res.data.type;
        this.typeOther = res.data.type_other;
        this.supervisorName = res.data.supervisor_name;
        this.organization = res.data.organization;
        this.hours = res.data.hours;
        this.description = res.data.description;
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async onSubmit() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.isLoading = true;
        try {
          const data = {
            name: this.name,
            type: this.type,
            type_other: this.typeOther,
            supervisor_name: this.supervisorName,
            organization: this.organization,
            hours: this.hours,
            description: this.description,
          };

          if (this.$route.params.id) {
            await this.updateActivity({ id: this.$route.params.id, data });
            this.makeToast({ variant: 'success', msg: 'Activity updated!' });
          } else {
            const createPayload = {
              ...data,
              ...(this.checklistContext.uniId && {
                entity_type: 'university',
                entity_id: this.checklistContext.uniId,
              }),
              ...(this.checklistContext.uniId && this.checklistContext.programId && {
                program_id: this.checklistContext.programId,
              }),
            };
            const resp = await this.createActivity(createPayload);
            this.makeToast({ variant: 'success', msg: 'Activity added!' });
            if (resp.data.data.points_earned > 0) {
              this.updateShowEarnedPointToast(resp.data.data.points_earned);
            } else {
              this.makeToast({
                variant: 'info',
                msg: 'Saved successfully. No checklist points awarded for this program requirement.',
              });
            }
          }
          setTimeout(() => {
            this.$router.push({ name: 'my-activities' });
          }, 1000);
        } catch (err) {
          this.makeToast({
            variant: 'danger',
            msg: get(err, 'response.data.message') || get(err, 'response.data.error') || this.$t('generalMsgs.genErrorMsg'),
          });
        }
        this.isLoading = false;
      } else {
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields.' });
      }
    },
  },

  async mounted() {
    if (this.$route.params.id) {
      this.fetchExisting();
    }
  },
};
</script>
